import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { TenantIdContext } from '../../../../components/app/App';
import {
  getTerminologyData,
  addTerminologyEntry,
  updateTerminologyEntry,
  removeTerminologyEntry,
  exportTerminology,
} from '../../services/api-calls';
import { DynamicTable } from '../../../../global/dynamic-table/DynamicTable';
import { EditableInputCell } from '../../../../global/dynamic-table/EditableInputCell';
import TableActionButtons from '../../../../global/table-action-buttons/TableActionButtons';
import ConfirmationDialog from '../../../../global/confirmation-dialog/ConfirmationDialog';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Input from '@amzn/meridian/input';
import { nanoid } from 'nanoid';

const LOCALE_REGEX = /^[a-z]{2}-[A-Z]{2}$/;

// Backend caps pageSize at 200. Terminology arcs are capped at 500 entries per
// the self-serve SOP, so a full arc is ~3 pages; MAX_PAGES bounds the cursor
// loop well above that while still guarding against an unbounded loop. If the
// cap ever trips, the UI shows a truncation warning rather than silently
// presenting a partial arc as complete.
const PAGE_SIZE = 200;
const MAX_PAGES = 25;

const createEmptyRow = (sourceLocale = '', targetLocale = '') => ({
  id: nanoid(),
  sourceTerm: '',
  targetTerm: '',
  sourceLocale,
  targetLocale,
  isNew: true,
  isEdit: false,
  isValid: true,
  original: {},
});

/**
 * Schema-agnostic terminology data table.
 * Frontend uses logical fields only — backend handles PK/SK translation.
 */
const TerminologyTable = () => {
  const tenantId = useContext(TenantIdContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fetchIncomplete, setFetchIncomplete] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteCallback, setDeleteCallback] = useState(() => () => {});
  const [sourceLocaleInput, setSourceLocaleInput] = useState('');
  const [targetLocaleInput, setTargetLocaleInput] = useState('');
  const [localeFilter, setLocaleFilter] = useState('');
  const [browseTrigger, setBrowseTrigger] = useState(0);
  const abortRef = useRef(null);

  const isLocaleValid = locale => LOCALE_REGEX.test(locale);
  const canBrowse = isLocaleValid(sourceLocaleInput) && isLocaleValid(targetLocaleInput);

  const handleBrowse = () => {
    if (!canBrowse) return;
    setLocaleFilter(`${sourceLocaleInput}:${targetLocaleInput}`);
    setBrowseTrigger(prev => prev + 1);
  };

  const handleLocaleKeyDown = e => {
    if (e.key === 'Enter' && canBrowse) handleBrowse();
  };

  const handleExport = async () => {
    if (!tenantId) return;
    setExporting(true);
    try {
      const response = await exportTerminology({ tenantId });
      if (response) {
        const csvText = response.data ?? '';
        const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${tenantId}-terminology.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      }
    } finally {
      setExporting(false);
    }
  };

  const fetchData = useCallback(
    async signal => {
      if (!tenantId || !localeFilter) return;
      setData([]);
      setFetchIncomplete(false);
      setLoading(true);
      try {
        const [sourceLocale, targetLocale] = (localeFilter || '').split(':');
        // Auto-load every page for the arc so the table (and its client-side
        // search/pagination) cover all entries. Previously only the first page
        // (~50) was fetched, leaving the rest unreachable — entries that sort
        // late (e.g. lowercase source terms) appeared "missing" though present.
        const allRows = [];
        let cursor = null;
        let pages = 0;
        do {
          const response = await getTerminologyData({
            tenantId,
            sourceLocale,
            targetLocale,
            pageSize: PAGE_SIZE,
            lastKey: cursor,
          });
          if (signal?.aborted) return;
          if (!response?.data) {
            // A page failed mid-pagination (the api layer already surfaced the
            // error banner). Don't render the partial result as complete — an
            // operator seeing "Showing 100 entries" would re-add "missing"
            // entries and create duplicates. Leave the table empty; the error
            // banner tells them to retry Browse.
            setData([]);
            setFetchIncomplete(true);
            return;
          }
          const rows = (response.data.items || []).map(item => ({
            ...item,
            id: nanoid(),
            isNew: false,
            isEdit: false,
            isValid: true,
            original: { ...item },
          }));
          allRows.push(...rows);
          cursor = response.data.lastKey || null;
          pages += 1;
        } while (cursor && pages < MAX_PAGES);
        if (signal?.aborted) return;
        // cursor still set after MAX_PAGES means the arc is larger than the
        // loop budget — show what we have but warn rather than claim complete.
        setFetchIncomplete(Boolean(cursor));
        setData([...allRows, createEmptyRow(sourceLocale, targetLocale)]);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [tenantId, localeFilter]
  );

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    fetchData(ac.signal);
    return () => ac.abort();
  }, [tenantId, localeFilter, browseTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCellChange = (rowIndex, columnId, value) => {
    setData(prev => prev.map((row, i) => (i === rowIndex ? { ...row, [columnId]: value } : row)));
  };

  const handleEdit = (rowIndex, isEdit) => {
    setData(prev =>
      prev.map((row, i) => {
        if (i !== rowIndex) return row;
        return isEdit ? { ...row, isEdit: true } : { ...row.original, original: { ...row.original } };
      })
    );
  };

  const handleSave = async (rowIndex, rowItem) => {
    const entry = {
      sourceTerm: rowItem.values.sourceTerm,
      targetTerm: rowItem.values.targetTerm,
      sourceLocale: rowItem.values.sourceLocale,
      targetLocale: rowItem.values.targetLocale,
    };
    if (rowItem.original.isNew) {
      const response = await addTerminologyEntry({ tenantId, entry });
      if (response?.data) {
        setData(prev => {
          const updated = prev.map((row, i) =>
            i === rowIndex ? { ...row, ...entry, isNew: false, isEdit: false, original: { ...row, ...entry } } : row
          );
          return [...updated, createEmptyRow(entry.sourceLocale, entry.targetLocale)];
        });
      }
    } else {
      const updateResponse = await updateTerminologyEntry({
        tenantId,
        entry: {
          ...entry,
          originalSourceTerm: rowItem.original.sourceTerm,
          originalSourceLocale: rowItem.original.sourceLocale,
          originalTargetLocale: rowItem.original.targetLocale,
        },
      });
      if (updateResponse?.data) {
        setData(prev => prev.map((row, i) => (i === rowIndex ? { ...row, isEdit: false, original: { ...row } } : row)));
      }
    }
  };

  const handleDelete = (rowIndex, rowItem) => {
    setDeleteCallback(() => async () => {
      const deleteResponse = await removeTerminologyEntry({
        tenantId,
        entry: {
          sourceTerm: rowItem.original.sourceTerm,
          sourceLocale: rowItem.original.sourceLocale,
          targetLocale: rowItem.original.targetLocale,
        },
      });
      if (deleteResponse?.data) {
        setData(prev => prev.filter((_, i) => i !== rowIndex));
      }
    });
    setDeleteDialogOpen(true);
  };

  const makeEditableCell =
    (accessor, validator) =>
    ({ value, column, row }) =>
      (
        <EditableInputCell
          row={row}
          column={column}
          value={value}
          onChange={v => handleCellChange(row.index, column.id, v)}
          validationHandler={validator}
        />
      );

  const requiredValidator = async v => {
    if (!v?.trim()) throw Error('Required');
  };
  const localeValidator = async v => {
    if (!LOCALE_REGEX.test(v)) throw Error('Format: xx-XX');
  };

  const columns = [
    {
      Header: 'Source Term',
      accessor: 'sourceTerm',
      width: 200,
      Cell: makeEditableCell('sourceTerm', requiredValidator),
    },
    {
      Header: 'Target Term',
      accessor: 'targetTerm',
      width: 200,
      Cell: makeEditableCell('targetTerm', requiredValidator),
    },
    {
      Header: 'Source Locale',
      accessor: 'sourceLocale',
      width: 120,
      Cell: makeEditableCell('sourceLocale', localeValidator),
    },
    {
      Header: 'Target Locale',
      accessor: 'targetLocale',
      width: 120,
      Cell: makeEditableCell('targetLocale', localeValidator),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      width: 175,
      disableSortBy: true,
      Cell: ({ row }) => (
        <TableActionButtons
          editable={true}
          removable={!row.original.isNew}
          row={row}
          onEdit={item => handleEdit(item.index, true)}
          onDiscardEdits={item => handleEdit(item.index, false)}
          onSave={item => handleSave(item.index, item)}
          onRemove={item => handleDelete(item.index, item)}
        />
      ),
    },
  ];

  return (
    <Container fluid className="p-3">
      <Row className="mb-3 align-items-center">
        <Col xs={4}>
          <Text type="h100">Terminology Entries</Text>
        </Col>
        <Col xs={2}>
          <Input
            value={sourceLocaleInput}
            onChange={setSourceLocaleInput}
            onKeyDown={handleLocaleKeyDown}
            placeholder="e.g. en-US"
            size="small"
            error={sourceLocaleInput && !isLocaleValid(sourceLocaleInput) ? 'Format: xx-XX' : undefined}
          />
        </Col>
        <Col xs="auto" className="px-1 d-flex align-items-center">
          <Text type="b200">→</Text>
        </Col>
        <Col xs={2}>
          <Input
            value={targetLocaleInput}
            onChange={setTargetLocaleInput}
            onKeyDown={handleLocaleKeyDown}
            placeholder="e.g. de-DE"
            size="small"
            error={targetLocaleInput && !isLocaleValid(targetLocaleInput) ? 'Format: xx-XX' : undefined}
          />
        </Col>
        <Col xs="auto">
          <Button type="secondary" size="small" onClick={handleBrowse} disabled={!canBrowse}>
            Browse
          </Button>
        </Col>
        <Col xs="auto">
          <Button type="secondary" size="small" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Exporting…' : 'Export All CSV'}
          </Button>
        </Col>
      </Row>
      {!localeFilter ? (
        <Row className="mt-3">
          <Col>
            <Text type="b200" color="secondary">
              Enter source and target locales above, then click Browse.
            </Text>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="mb-2">
            <Col>
              <Text type="b200" color="secondary">
                {loading
                  ? 'Loading all entries…'
                  : `Showing ${Math.max(data.length - 1, 0)} ${
                      data.length - 1 === 1 ? 'entry' : 'entries'
                    } for ${localeFilter.replace(':', ' → ')}${
                      fetchIncomplete ? ' (results may be incomplete — try Export All CSV for the full set)' : ''
                    }`}
              </Text>
            </Col>
          </Row>
          <Row>
            <DynamicTable columns={columns} data={data} getRowId={row => row.id} />
          </Row>
        </>
      )}
      <ConfirmationDialog
        isOpen={deleteDialogOpen}
        setIsOpen={setDeleteDialogOpen}
        text="Delete this terminology entry?"
        onConfirm={deleteCallback}
      />
    </Container>
  );
};

export default TerminologyTable;
